import React from 'react'
import { motion } from 'framer-motion'
import { Skeleton } from '../../ui/skeleton'
import { cn } from '../../../lib/utils'
import {
  ANIMATION_DELAYS,
  ANIMATION_DURATIONS,
  fadeIn,
  fadeUp,
  fadeUpShort,
  slideInLeft,
  slideInLeftShort,
  scaleIn,
  slideUp,
  withDelay
} from '../../../lib/animations'

/**
 * Centralized loading shim component following the skeleton pattern
 * Enhanced with glow effects and beautiful typography to guide users through loading states
 * 
 * @param {Object} props
 * @param {'default'|'list'|'card'|'table'|'message'|'thread'|'dashboard'} props.variant - The type of loading state
 * @param {string} [props.label] - Optional label text to display
 * @param {number} [props.count] - Number of skeleton items to render (for list/table variants)
 * @param {string} [props.className] - Additional CSS classes
 */
export const LoadingShim = ({ 
  variant = 'default', 
  label,
  count = 5,
  className
}) => {
  // Lazy evaluation - only create the JSX for the selected variant
  switch (variant) {
    case 'list':
      return <ListShim count={count} className={className} label={label} />
    case 'card':
      return <CardShim count={count} className={className} label={label} />
    case 'table':
      return <TableShim count={count} className={className} label={label} />
    case 'message':
      return <MessageShim count={count} className={className} label={label} />
    case 'thread':
      return <ThreadShim count={count} className={className} label={label} />
    case 'dashboard':
      return <DashboardShim className={className} label={label} />
    default:
      return <DefaultShim label={label} className={className} />
  }
}

/**
 * Beautiful loading text component with animated dots and refined typography
 * Guides users through loading state with elegant, readable text
 * Automatically strips trailing dots/ellipses from label since animated dots are added
 */
const LoadingText = ({ children, className }) => {
  // Strip all trailing dots since we add animated dots
  const text = typeof children === 'string'
    ? children.replace(/\.+$/, '').trim()
    : children

  return (
    <motion.div
      className={cn(
        "loading-text text-base font-medium tracking-tight",
        "text-foreground/80",
        className
      )}
      {...fadeUp}
      transition={{ ...fadeUp.transition, ease: "easeOut" }}
    >
      <span className="inline-block">{text}</span>
      <span className="loading-dots inline-block ml-1" />
    </motion.div>
  )
}

/**
 * Default loading shim - skeleton cards with beautiful typography
 * No spinner - uses skeleton pattern with typography to guide users
 */
const DefaultShim = ({ label, className }) => (
  <motion.div
    className={cn(
      "flex flex-col items-center justify-center h-full w-full p-8 space-y-6",
      className
    )}
    {...fadeIn}
  >
    {/* Beautiful loading typography */}
    <motion.div
      className="text-center space-y-2"
      {...fadeUp}
      transition={withDelay(fadeUp.transition, ANIMATION_DELAYS.short)}
    >
      <LoadingText className="text-lg font-medium">
        {label || "Loading"}
      </LoadingText>
    </motion.div>
    
    {/* Skeleton cards that indicate content is loading */}
    <div className="w-full max-w-md space-y-3">
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          className="bg-card rounded-lg border border-border p-4 space-y-2"
          {...slideInLeft}
          transition={withDelay(slideInLeft.transition, ANIMATION_DELAYS.medium + i * ANIMATION_DELAYS.short)}
        >
          <Skeleton className="h-4 w-3/4 rounded" glow />
          <Skeleton className="h-3 w-full rounded" glow />
          <Skeleton className="h-3 w-5/6 rounded" glow />
        </motion.div>
      ))}
    </div>
  </motion.div>
)

/**
 * List loading shim - vertical list of skeleton items with glow
 */
const ListShim = ({ count, className, label }) => (
  <motion.div
    className={cn("space-y-2 p-4", className)}
    {...fadeIn}
  >
    {label && (
      <motion.div
        className="mb-4"
        {...fadeUpShort}
        transition={withDelay(fadeUpShort.transition, ANIMATION_DELAYS.short)}
      >
        <LoadingText>{label}</LoadingText>
      </motion.div>
    )}
    {Array.from({ length: count }).map((_, idx) => (
      <motion.div
        key={idx}
        className="flex items-center gap-3"
        {...slideInLeft}
        transition={withDelay(slideInLeft.transition, idx * ANIMATION_DELAYS.stagger)}
      >
        <Skeleton className="h-4 w-4 rounded-full" glow />
        <Skeleton className="h-4 flex-1 rounded" glow />
        <Skeleton className="h-4 w-20 rounded" glow />
      </motion.div>
    ))}
  </motion.div>
)

/**
 * Card loading shim - grid of card skeletons with glow
 */
const CardShim = ({ count, className, label }) => (
  <motion.div
    className={cn("space-y-4", className)}
    {...fadeIn}
  >
    {label && (
      <motion.div
        {...fadeUpShort}
        transition={withDelay(fadeUpShort.transition, ANIMATION_DELAYS.short)}
      >
        <LoadingText className="text-base">{label}</LoadingText>
      </motion.div>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
      {Array.from({ length: count }).map((_, idx) => (
        <motion.div
          key={idx}
          className="bg-card rounded-lg border border-border p-4 space-y-3"
          {...scaleIn}
          transition={withDelay(scaleIn.transition, idx * ANIMATION_DELAYS.short)}
        >
          <Skeleton className="h-4 w-3/4 rounded" glow />
          <Skeleton className="h-6 w-1/2 rounded" glow />
          <Skeleton className="h-3 w-full rounded" glow />
        </motion.div>
      ))}
    </div>
  </motion.div>
)

/**
 * Table loading shim - table row skeletons with glow
 */
const TableShim = ({ count, className, label }) => (
  <motion.div
    className={cn("space-y-2 p-4", className)}
    {...fadeIn}
  >
    {label && (
      <motion.div
        className="mb-4"
        {...fadeUpShort}
        transition={withDelay(fadeUpShort.transition, ANIMATION_DELAYS.short)}
      >
        <LoadingText>{label}</LoadingText>
      </motion.div>
    )}
    {/* Table header */}
    <div className="flex gap-4 pb-2 border-b border-border">
      <Skeleton className="h-4 w-24 rounded" glow />
      <Skeleton className="h-4 w-32 rounded" glow />
      <Skeleton className="h-4 w-20 rounded" glow />
      <Skeleton className="h-4 w-16 rounded ml-auto" glow />
    </div>
    {/* Table rows */}
    {Array.from({ length: count }).map((_, idx) => (
      <motion.div
        key={idx}
        className="flex gap-4 py-2"
        {...slideInLeftShort}
        transition={withDelay(slideInLeftShort.transition, idx * ANIMATION_DELAYS.stagger)}
      >
        <Skeleton className="h-4 w-24 rounded" glow />
        <Skeleton className="h-4 w-32 rounded" glow />
        <Skeleton className="h-4 w-20 rounded" glow />
        <Skeleton className="h-4 w-16 rounded ml-auto" glow />
      </motion.div>
    ))}
  </motion.div>
)

/**
 * Message loading shim - message bubbles (inbound/outbound) with glow
 */
const MessageShim = ({ count, className, label }) => (
  <motion.div
    className={cn("flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 py-3", className)}
    {...fadeIn}
  >
    {label && (
      <motion.div
        className="mb-4 text-center"
        {...fadeIn}
        transition={withDelay(fadeIn.transition, ANIMATION_DELAYS.short)}
      >
        <LoadingText>{label}</LoadingText>
      </motion.div>
    )}
    {Array.from({ length: count }).map((_, idx) => {
      const isOutbound = idx % 3 === 0
      // Simplified margin calculation: first item no margin, even items larger margin, odd items smaller margin
      const getMessageMargin = (index) => {
        if (index === 0) return 'mt-0'
        return index % 2 === 0 ? 'mt-3.5' : 'mt-1.5'
      }
      
      return (
        <motion.div
          key={idx}
          className={cn('flex flex-col', getMessageMargin(idx), isOutbound ? 'items-end' : 'items-start')}
          {...fadeUp}
          transition={withDelay(fadeUp.transition, idx * ANIMATION_DELAYS.messageStagger)}
        >
          <div className={cn(
            'mb-1 flex items-baseline gap-2',
            isOutbound ? 'justify-end' : 'justify-start'
          )}>
            <Skeleton className="h-3 w-20 rounded" glow />
            <Skeleton className="h-3 w-12 rounded" glow />
          </div>
          <Skeleton
            className={cn(
              'rounded-2xl',
              idx % 2 === 0 ? 'w-3/4' : 'w-2/3',
              idx % 3 === 0 ? 'h-16' : 'h-10'
            )}
            glow
          />
        </motion.div>
      )
    })}
  </motion.div>
)

/**
 * Thread loading shim - thread list items with glow
 */
const ThreadShim = ({ count, className, label }) => (
  <motion.div
    className={cn("h-full overflow-auto", className)}
    {...fadeIn}
  >
    {label && (
      <motion.div
        className="p-4 text-center"
        {...fadeIn}
        transition={withDelay(fadeIn.transition, ANIMATION_DELAYS.short)}
      >
        <LoadingText>{label}</LoadingText>
      </motion.div>
    )}
    {Array.from({ length: count }).map((_, idx) => (
      <motion.div
        key={idx}
        className="relative w-full pl-3 pr-2 py-2 mb-1 bg-card border border-border rounded-md shadow-sm"
        {...slideInLeft}
        transition={withDelay(slideInLeft.transition, idx * ANIMATION_DELAYS.stagger)}
      >
        <div className="flex-1 overflow-hidden space-y-1">
          <div className="flex justify-between items-center">
            <Skeleton className="h-4 w-24 rounded" glow />
            <Skeleton className="h-3.5 w-12 rounded" glow />
          </div>
          <Skeleton className="h-4 w-4/5 rounded" glow />
          <div className="flex gap-1 mt-1">
            <Skeleton className="h-3 w-9 rounded-full" glow />
            <Skeleton className="h-3 w-8 rounded-full" glow />
          </div>
        </div>
      </motion.div>
    ))}
  </motion.div>
)

/**
 * Dashboard loading shim - dashboard layout with metrics and charts, enhanced with glow
 */
const DashboardShim = ({ className, label }) => (
  <motion.div
    className={cn("space-y-6 p-6", className)}
    {...fadeIn}
  >
    {label && (
      <motion.div
        className="text-center mb-6"
        {...fadeUpShort}
        transition={withDelay(fadeUpShort.transition, ANIMATION_DELAYS.short)}
      >
        <LoadingText className="text-base">{label}</LoadingText>
      </motion.div>
    )}
    
    {/* Header */}
    <motion.div
      className="space-y-3"
        {...fadeUpShort}
        transition={withDelay(fadeUpShort.transition, ANIMATION_DELAYS.medium)}
      >
      <Skeleton className="h-8 w-64 rounded" glow />
      <Skeleton className="h-4 w-96 rounded" glow />
    </motion.div>
    
    {/* Metrics cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[1, 2, 3].map((i, idx) => (
        <motion.div
          key={i}
          className="bg-card rounded-lg border border-border p-4 space-y-3"
          {...scaleIn}
          transition={withDelay(scaleIn.transition, ANIMATION_DELAYS.long + idx * ANIMATION_DELAYS.short)}
        >
          <Skeleton className="h-4 w-24 rounded" glow />
          <Skeleton className="h-8 w-20 rounded" glow />
          <Skeleton className="h-3 w-32 rounded" glow />
        </motion.div>
      ))}
    </div>
    
    {/* Chart */}
    <motion.div
      className="bg-card rounded-lg border border-border p-6 space-y-4"
      {...slideUp}
      transition={withDelay(slideUp.transition, ANIMATION_DELAYS.long * 2)}
    >
      <Skeleton className="h-5 w-48 rounded" glow />
      <Skeleton className="h-64 w-full rounded" glow />
    </motion.div>
  </motion.div>
)

export default LoadingShim
